import React, { useState } from 'react';
import { Field } from 'formik';
import {
  Grid,
  Typography,
  FormControl,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box
} from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';

import { FormikCheckbox } from '../../../../shared/components/form/FormikCheckbox';
import FormikTextField from '../../../../shared/components/form/FormikTextField';
import TextField from '../../../../shared/components/form/fields/TextField';
import SearchableStaffDropdown from '../../shared/dropdowns/SearchableStaffDropdown';
import GlobalButton from '../../../../shared/components/UI/GlobalButton';
import withUserContext from '../../../../shared/contexts/userContext/withUserContext';
import { getCurrencyCodeFromUserContext } from '../../../../shared/utils/helpers';
import { formatAmount } from '../../../../shared/utils/formatters';

const ContractValueLimitSection = ({ values, setFieldValue, userContext }) => {
  const { contractValueLimitEnabled, contractValueLimit, signerOverrides } = values;
  
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [overrideLimit, setOverrideLimit] = useState('');

  const handleEnableChange = e => {
    const enabled = e.target.checked;
    setFieldValue('contractValueLimitEnabled', enabled);
    if (!enabled) {
      setFieldValue('contractValueLimit', '');
      setFieldValue('signerOverrides', []);
    }
  };

  const handleAddOverride = () => {
    if (!selectedStaff || !overrideLimit || parseFloat(overrideLimit) <= 0) {
      return;
    }

    // Check if staff already exists in overrides
    const existingIndex = signerOverrides.findIndex(
      override => override.staffId === selectedStaff.value
    );

    if (existingIndex >= 0) {
      // Update existing override
      const newOverrides = [...signerOverrides];
      newOverrides[existingIndex] = {
        staffId: selectedStaff.value,
        staffName: selectedStaff.label,
        limit: parseFloat(overrideLimit)
      };
      setFieldValue('signerOverrides', newOverrides);
    } else {
      // Add new override
      setFieldValue('signerOverrides', [
        ...signerOverrides,
        {
          staffId: selectedStaff.value,
          staffName: selectedStaff.label,
          limit: parseFloat(overrideLimit)
        }
      ]);
    }

    // Reset form
    setSelectedStaff(null);
    setOverrideLimit('');
  };

  const handleRemoveOverride = staffId => {
    setFieldValue(
      'signerOverrides',
      signerOverrides.filter(override => override.staffId !== staffId)
    );
  };

  // Get IDs of staff already in overrides list
  const excludedStaffIds = signerOverrides.map(override => override.staffId);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h3" gutterBottom>
          Contract Value Limits
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Field
          name="contractValueLimitEnabled"
          component={FormikCheckbox}
          label="Limit Contract Total Amount"
          onChange={handleEnableChange}
        />
      </Grid>

      {contractValueLimitEnabled && (
        <>
          <Grid item xs={12} md={6}>
            <Field
              name="contractValueLimit"
              component={FormikTextField}
              label="Contract Value Limit"
              type="number"
              placeholder="0.00"
              fullWidth
              required
              inputProps={{ min: "0", step: "0.01" }}
              helperText="Base limit for all users"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h3" gutterBottom>
              User Override Total Amount
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Set custom limits for specific users that override the base limit above.
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <SearchableStaffDropdown
                label="Select User"
                value={selectedStaff}
                onChange={setSelectedStaff}
                placeholder="Select a user"
                selectedOptions={signerOverrides.map(o => ({
                  value: o.staffId,
                  label: o.staffName
                }))}
                isClearable
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={5}>
            <TextField
              value={overrideLimit}
              onChange={e => setOverrideLimit(e.target.value)}
              label="Override Limit"
              type="number"
              placeholder="0.00"
              fullWidth
              inputProps={{ min: "0", step: "0.01" }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Box display="flex" alignItems="flex-end" height="100%">
              <GlobalButton
                variant="primary"
                handleClick={handleAddOverride}
                disabled={!selectedStaff || !overrideLimit || parseFloat(overrideLimit) <= 0}
                fullWidth
              >
                Add
              </GlobalButton>
            </Box>
          </Grid>

          {signerOverrides && signerOverrides.length > 0 && (
            <Grid item xs={12}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User Name</TableCell>
                    <TableCell align="right">Limit Amount</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signerOverrides.map(override => (
                    <TableRow key={override.staffId}>
                      <TableCell>{override.staffName}</TableCell>
                      <TableCell align="right">
                        {formatAmount(override.limit, getCurrencyCodeFromUserContext(userContext))}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveOverride(override.staffId)}
                          aria-label="Remove override"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

ContractValueLimitSection.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  userContext: PropTypes.object
};

export default withUserContext(ContractValueLimitSection);

